import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, Form, message } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import rolesConstants, { RolesTypes } from '@utils/rolesConstants';
import Actions from '@components/Actions';
import api from '@services/api';
import ActionsItems from '@components/Actions/ActionsItems';
import { DeviceActionProps } from '@components/Actions/ActionsModal/types';

export interface LorawanActionsModalProps {
  visible: boolean;
  onClose: (cancelled?: boolean) => void;
  actionRole: RolesTypes | undefined;
  selectedDevices: any;
  isUpload?: boolean;
}

const LorawanActionsModal = ({
  visible,
  onClose,
  actionRole,
  selectedDevices,
  isUpload,
}: LorawanActionsModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('device-actions-modal');
  const { selectedApp, isMot } = useContext(SessionContext);
  const [form] = Form.useForm();
  const uploadedItems = Form.useWatch('uploadedItems', form);

  const [sendDeviceLoading, setSendDeviceLoading] = useState(false);

  const postAction = useCallback(
    (payload: any) => api.post('/service-proxy/lorawan/actions', payload),
    [],
  );

  const devices = useMemo(
    () => (isUpload ? uploadedItems : selectedDevices),
    [isUpload, selectedDevices, uploadedItems],
  );

  const transfersAction = ActionsItems.LoraWan.useTransfers({
    isUpload,
    devices,
    form,
  });

  const importAction = ActionsItems.LoraWan.useImport({
    devices,
  });

  const deactivationAction = ActionsItems.LoraWan.useDeactivation({
    isUpload,
    devices,
  });

  const uplinkBlockUnlockAction = ActionsItems.LoraWan.useUplinkBlockUnlock({
    isUpload,
    devices,
    form,
  });

  const downlinkBlockUnlockAction = ActionsItems.LoraWan.useDownlinkBlockUnlock(
    {
      isUpload,
      devices,
      form,
    },
  );

  const changeDescriptionAction = ActionsItems.LoraWan.useChangeDescription({
    isUpload,
    devices,
    form,
  });

  const changeTagsAction = ActionsItems.LoraWan.useChangeTags({
    isUpload,
    devices,
    form,
  });

  const downlinkScheduleAction = ActionsItems.LoraWan.useDownlinkSchedule({
    isUpload,
    devices,
    form,
  });

  const selectedAction = useMemo((): DeviceActionProps | undefined => {
    const actions: { [key: string]: DeviceActionProps } = {
      [rolesConstants.LORAWAN_TRANSFERS]: transfersAction,
      [rolesConstants.LORAWAN_IMPORT]: importAction,
      [rolesConstants.LORAWAN_DEACTIVATION]: deactivationAction,
      [rolesConstants.LORAWAN_UPLINK_BLOCK_UNLOCK]: uplinkBlockUnlockAction,
      [rolesConstants.LORAWAN_DOWNLINK_BLOCK_UNLOCK]: downlinkBlockUnlockAction,
      [rolesConstants.LORAWAN_CHANGE_DESCRIPTION]: changeDescriptionAction,
      [rolesConstants.LORAWAN_CHANGE_TAGS]: changeTagsAction,
      [rolesConstants.LORAWAN_DOWNLINK_SCHEDULE]: downlinkScheduleAction,
    };

    return actionRole && actions[actionRole];
  }, [
    transfersAction,
    importAction,
    deactivationAction,
    uplinkBlockUnlockAction,
    downlinkBlockUnlockAction,
    changeDescriptionAction,
    changeTagsAction,
    downlinkScheduleAction,
    actionRole,
  ]);

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}/${
      isMot ? 'lorawan/' : ''
    }operational/actions-queue?actionType=${
      selectedAction?.payloadToSend?.action
    }`;
    message.success(
      <>
        <span>{t('msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('msgs.follow-in-the-actions-queue')}
        </Button>
      </>,
      2,
    );
  }, [selectedApp, isMot, selectedAction?.payloadToSend?.action, t, navigate]);

  const handleOnClose = useCallback(() => {
    onClose();
    form.resetFields();
  }, [form, onClose]);

  const handleActionConfirm = useCallback(async () => {
    setSendDeviceLoading(true);
    try {
      if (selectedAction?.sendDeviceAction) {
        selectedAction?.sendDeviceAction();
      } else {
        await postAction(selectedAction?.payloadToSend);
        handleSuccess();
      }
    } catch {
      message.error(t('msgs.error-when-requesting'));
    }
    handleOnClose();
    setSendDeviceLoading(false);
  }, [handleOnClose, selectedAction, postAction, handleSuccess, t]);

  return (
    <Actions.Modal
      form={form}
      isOpen={visible}
      isUpload={isUpload}
      onClose={handleOnClose}
      onConfirm={handleActionConfirm}
      onConfirmLoading={sendDeviceLoading}
      selectedAction={selectedAction}
    />
  );
};

export default LorawanActionsModal;
